<script lang="ts">
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import { onMount } from 'svelte';
  import { setupProjectAnimations } from '$lib/utils/animations';
  import TeachingSection from '$lib/components/TeachingSection.svelte';
  import ScrambleIn from '$lib/components/ScrambleIn.svelte';

  onMount(() => {
    setupProjectAnimations();
  });
</script>

<ProjectHeroSplit
  title="Is This Thing On?"
  client="FH Potsdam & UdK Berlin"
  industry="Higher Education"
  product="Knowledge transfer"
  heroMedia={{
    type: 'image',
    src: '/projects/itto/design-fiction.png'
  }}
  metrics={[
    { value: 60, label: "Students" },
    { value: 8, label: "Institutions" },
    { value: 6, label: "Courses" }
  ]}
  roles="Lecturer and curriculum writer"
/>

<!-- Large Text Block -->
<div class="project-section">
  <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6">
    <p class="large-text text-black/90 animate-on-view">
      A selection of teaching engagements in interface design, information visualization, and design futures at leading German institutions including University of Arts Berlin, University of Science Potsdam, and Academy of Fine Arts Dresden.
    </p>
  </div>
</div>

<div class="project-section">
  <div class="project-grid">
    <div class="project-column">
      <ScrambleIn text="Is This Thing On?" className="text-2xl font-light mt-16 mb-8" />
      <p class="text-sm text-gray-500 mb-2">
        University of Applied Sciences Potsdam<br>
        Interface Design, Winter Semester 2011/12<br>
        Co-taught with Steffen Fiedler (Studio NAND)
      </p>
    </div>
  </div>
</div>




<div class="project-section">
<p>
  This course combined speculative design, technical prototyping, and social inquiry to explore how emerging technologies shape perception, power, and meaning.
</p>
</div>

<!-- Gallery Section -->
<div class="max-w-6xl mx-auto px-4 md:px-8 py-16">
  <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
    <figure>
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[20rem]">
        <img src="/projects/itto/installation1.jpg" alt="Talking Machines" class="w-full h-80 object-[hero-full] animate-on-view" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">Talking Machines by Sören Knoll: An installation where machine-to-machine communication evolves JFK's historic speech through endless reinterpretation and real-time sentiment analysis — visualized on an analog drum plotter.</figcaption>
    </figure>
    <figure>
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[20rem]">
        <img src="/projects/itto/itto-words-of-a-middle-man.jpg" alt="Words of a Middle Man" class="w-full h-80 object-[hero-full] animate-on-view" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">Words of a Middle Man by Teutenberg, Steinerner & Volker: A custom router translating machine communication on a local network into human-readable narratives.</figcaption>
    </figure>
    <figure>
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[20rem]">
        <img src="/projects/itto/romanbet_01.jpg" alt="The Romantic Observer" class="w-full h-80 object-[hero-full] animate-on-view" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">The Romantic Observer by Serowski & Ebhardt: A poetic machine that awakens at sunset to measure and analyze airborne dust particles — creating an emotional symbiosis with its environment.</figcaption>
    </figure>
    <figure>
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[20rem]">
        <img src="/projects/itto/a.jpg" alt="Interest Compression" class="w-full h-80 object-[hero-full] animate-on-view" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">Interest Compression by Alyssa Trawkina: A feedback system that listens to literary texts, detects uninterested segments, and edits the story in real-time to retain only the most engaging content.</figcaption>
    </figure>
    <figure class="md:col-span-2">
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[20rem]">
        <img src="/projects/itto/IMG_7548.JPG" alt="Installation 6" class="w-full h-180 object-[hero-full] animate-on-view" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">
        Noam: A device that senses temperature, light, and sound, generating haikus to explore the interplay between objective data and subjective perception.
      </figcaption>
    </figure>
  </div>
</div>

<!-- Berlin Mitte Section -->
<div class="project-section">
  <div class="project-grid">
    <div class="project-column">
      <ScrambleIn text="Berlin Mitte" className="text-2xl font-light mt-16 mb-8" />
      <p class="text-sm text-gray-500 mb-8">
        UdK Berlin<br>
        Visual Systems Class<br>
        With Prof. David Skopec<br>
      </p>
    </div>
    <div class="project-column-wide">
      <div class="space-y-6">
        <p>
          The semester project <em>Mitte Berlin</em> in the class of Visual Systems focused on Berlin's geographical center. This seemingly unremarkable green strip in a residential area of Kreuzberg served as a starting point for research, analysis, and transformation.
        </p>
        <p>
          Geographical centers can be determined in countless ways. But why is the focus always on the geographic center? In the end, an ultimate center is defined based on all the individual centers.
        </p>
        <p>
          Students developed methodologies for analyzing and representing urban space through systematic visual approaches. The project combined cartographic methods, demographic data, and visual analysis to create comprehensive studies of Berlin's center.
        </p>      
      </div>
    </div>
  </div>
</div>

<!-- Gallery Section -->
<div class="max-w-6xl mx-auto px-4 md:px-8 py-16">
  <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
    <figure>
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[20rem]">
        <img src="https://infoklasse.de/wp-content/uploads/2021/12/160717_mittenmang-011-3840x2548.jpg" alt="Mittenmang 1" class="max-h-96 w-auto object-contain animate-on-view" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">Community gathering in the heart of the city</figcaption>
    </figure>
    <figure>
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[20rem]">
        <img src="https://infoklasse.de/wp-content/uploads/2021/12/160717_mittenmang-0112-3840x2548.jpg" alt="Mittenmang 2" class="max-h-96 w-auto object-contain animate-on-view" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">Urban landscape and open space</figcaption>
    </figure>
    <figure>
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[20rem]">
        <img src="https://infoklasse.de/wp-content/uploads/2021/12/160717_mittenmang-0113-3840x2548.jpg" alt="Mittenmang 3" class="max-h-96 w-auto object-contain animate-on-view" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">Everyday life in Mittenmang</figcaption>
    </figure>
    <figure>
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[20rem]">
        <img src="https://infoklasse.de/wp-content/uploads/2021/12/160717_mittenmang-0114-3840x2548.jpg" alt="Mittenmang 4" class="max-h-96 w-auto object-contain animate-on-view" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">Architectural details and textures</figcaption>
    </figure>
    <figure class="md:col-span-2">
      <div class="w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center min-h-[32rem]">
        <img src="https://infoklasse.de/wp-content/uploads/2021/12/160717_mittenmang-0115-3840x2548.jpg" alt="Mittenmang 5" class="h-full w-full object-cover animate-on-view rounded" />
      </div>
      <figcaption class="mt-2 text-sm text-gray-500">Panoramic view of the Mittenmang site</figcaption>
    </figure>
  </div>
</div>

<ProjectNav 
  prevProject={{
    title: "The Gestalt of Digital Identity",
    url: "/projects/gestalt-digital-identity"
  }}
  nextProject={{
    title: "Framing Art Science Technology",
    url: "/projects/framing-art-science-technology"
  }}
/>

<Footer />
